import styled from 'styled-components';
import { size } from '../layout/helpers';
import { theme } from '../layout/theme';
import { Badge } from '../Badge/wrappers';
import { PriceDesign as PriceDesignBase } from '../Price/wrappers';
import { Image } from '../Image';

export const ProductImage = styled.div`
  aspect-ratio: 2 / 2.5;
  background-color: ${theme.colors.bg.main};
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: ${size(1)};
  min-width: 0;
  position: relative;
`;

export const PrimaryImage = styled.div`
  height: 100%;
  height: calc(100% - (56px * 2));
  margin: auto;
  position: relative;
  width: 100%;
  width: calc(100% - (16px * 2));

  & img {
    height: 100%;
    object-fit: contain;
    position: absolute;
    top: 0;
  }
`;

export const SecondaryImage = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0;
  overflow: hidden;
  transition: opacity 350ms ease-in-out;
  background: white;

  &.show {
    opacity: 1;
  }

  & img {
    height: 100%;
  }
`;

export const Header = styled.div`
  flex-shrink: 0;
  position: relative;
`;

export const Markings = styled.div`
  display: flex;
  height: ${size(4)};
  padding-top: ${size(2)};
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;

  &.push-down-ltMd {
    ${theme.screens.LtMd} {
      padding-top: ${size(1)};
    }
  }
`;

export const LeftMarking = styled.div`
  align-self: flex-start;
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-start;
  min-height: 24px;
`;

export const RightMarking = styled.div`
  align-self: flex-start;
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-end;
  min-height: 24px;
`;

export const MarkingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${size(1)};
`;

export const Content = styled.div`
  margin: 0 ${size(1)};

  ${theme.screens.LtSm} {
    margin-top: ${size(1.5)};

    &.slim {
      margin-top: 0;
    }

    &.narrow-ltSM {
      margin: 0 ${size(1)};
    }
  }

  .tight & {
    ${theme.screens.LtSm} {
      margin-top: ${size(1)};
    }
  }

  &.horizontal {
    margin-left: ${size(1)};
  }
`;

export const Body = styled.div`
  margin-bottom: ${size(1)};
  flex: 1 1 auto;
`;

export const Footer = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  ${theme.screens.LtSm} {
    gap: ${size(0.5)};
    flex-direction: column;
    align-items: flex-start;
  }
  &.productCardFooter {
    display: flex;
    align-items: flex-end;
    ${theme.screens.LtSm} {
      align-items: stretch;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
    }
  }
`;

export const HotspotFooter = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  ${theme.screens.LtSm} {
    gap: ${size(0.5)};
    flex-direction: column;
  }
`;

export const Variants = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ColorList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin: ${size(-0.5)};

  ${theme.screens.LtSm} {
    margin: ${size(-0.375)};
  }
`;

type ColorProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  color: string;
};

export const Color = styled.div<ColorProps>`
  padding: ${size(0.5)};

  &:before {
    content: '';
    display: block;
    width: ${size(1)};
    height: ${size(1)};
    border-radius: 50%;
    background-color: ${(props) => props.color};
    box-shadow: ${(props) =>
      props.color === '#fff' || props.color === '#ffffff' || props.color === 'white'
        ? `0 0 0 0.6px ${theme.colors.border.contrast}`
        : undefined};
  }
`;

export const ColorCount = styled.div`
  flex-shrink: 0;
  padding: ${size(0.5)};
  font-size: 0.875rem;
  color: ${theme.colors.text.alt};
  line-height: 1;

  ${theme.screens.LtSm} {
    font-size: 0.625rem;
  }
`;

export const VariantText = styled.div`
  flex-shrink: 0;
  font-size: 0.875rem;
  font-weight: 400;

  ${theme.screens.LtSm} {
    font-size: 0.75rem;
  }
`;

export const Label = styled.div`
  display: flex;
  align-items: center;
  min-height: ${size(3)};

  &.higher {
    height: ${size(4)};
  }

  &.flags {
    flex-direction: row !important;
  }

  &.hotspotFlags {
    flex-direction: row !important;
    flex-wrap: wrap;
    gap: ${size(1)} !important;
  }

  ${theme.screens.LtSm} {
    min-height: ${size(2.5)};
  }

  &.cartFlags {
    position: absolute;
    top: ${size(-3)};
    left: ${size(3)};

    ${theme.screens.LtMd} {
      top: ${size(-2.5)};
      left: ${size(2)};
    }
  }
`;

export const Text = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  font-size: 1rem;
  font-weight: 600;
  margin-right: 0.25em;

  ${theme.screens.LtSm} {
    font-size: 0.875rem;
  }
`;

export const Title = styled.a`
  display: inline-block;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }

  &:before {
    content: '';
    position: absolute;
    z-index: 5;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export const Description = styled.div`
  display: inline;
  font-weight: ${theme.font.weight.regular};
  color: ${theme.colors.text.alt};

  .slim & {
    font-weight: ${theme.font.weight.thin};
    color: ${theme.colors.text.main};
  }
`;

export const DescriptionText = styled.div`
  color: ${theme.colors.text.alt};
  font-weight: ${theme.font.weight.regular};
  max-width: 90%;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 16px;

  ${theme.screens.LtSm} {
    font-size: 14px;
  }
`;

export const CampaignSlot = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding-bottom: ${size(2)};

  &.push-down-ltMd {
    ${theme.screens.LtMd} {
      padding-bottom: ${size(1)};
    }
  }
`;

export const Card = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  color: ${theme.colors.text.main};
  background: #faf9f8;
`;

export const SlimCard = styled(Card)`
  padding: ${size(3)} ${size(2)};
  background-color: ${theme.colors.bg.main};

  ${theme.screens.LtSm} {
    padding: ${size(1)};
  }

  ${Badge} {
    height: ${size(3)};
    font-size: 13px;
    ${theme.screens.LtSm} {
      font-size: 12px;
    }
  }

  ${Label} {
    align-items: flex-start;
    flex-direction: column;
    padding-bottom: ${size(1)};
  }
  ${Label}& .priceFlag {
    padding-bottom: ${size(15)};
  }
`;

export const SlimImage = styled(Image)`
  height: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
`;

export const Prices = styled.div`
  display: flex;
  align-items: baseline;
  flex-direction: column;
`;

export const Flag = styled.div<{ bg: string; textColor: string }>`
  width: auto;
  padding: 0 ${size(2)} 0 ${size(2)};
  color: ${(props) => props.textColor};
  background-color: ${(props) => props.bg};
  font-weight: 600;
  font-size: 14px;
  min-height: ${size(3)};
  height: ${size(4)};
  display: flex;
  align-items: center;
  justify-content: center;

  &.large {
    padding: ${size(0.5)} ${size(1)} ${size(0.5)} ${size(1)};
    height: 32px;

    ${theme.screens.LtSm} {
      height: 28px;
    }

    ${theme.screens.SmLtMd} {
      height: 30px;
    }
  }

  &.small {
    padding: ${size(0.5)} ${size(1)} ${size(0.5)} ${size(1)};
    height: 28px;

    ${theme.screens.LtSm} {
      height: 22px;
      font-size: 11px;
    }

    ${theme.screens.SmLtMd} {
      font-size: 12px;
      height: 28px;
    }

    ${theme.screens.MdLtLg} {
      font-size: 12px;
      height: 22px;
    }
  }

  &.product-page {
    ${theme.screens.LtSm} {
      padding: ${size(0.5)} ${size(1)} ${size(0.5)} ${size(1)};
    }
  }
`;

export const CustomCertification = styled.div`
  width: 80%;
  height: 80%;
`;

export const CustomCertificationImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const Certification = styled.div`
  margin-right: ${size(1)};

  &.large {
    width: 32px;
    height: 32px;
    ${theme.screens.LtSm} {
      width: 28px;
      height: 28px;
    }
  }

  &.small {
    width: 28px;
    height: 28px;
    ${theme.screens.LtSm} {
      width: 24px;
      height: 24px;
    }
    ${theme.screens.SmLtMd} {
      width: 28px;
      height: 28px;
    }
  }
`;

export const CertificationImage = styled.img`
  width: 100%;
`;

export const PriceCommunicationText = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  font-weight: 600;
  font-size: 14px;
`;

export const PriceDesign = styled(PriceDesignBase)`
  font-size: 1.25rem;
  padding: 2px 6px 2px 6px;
  &.sm {
    ${theme.screens.LtSm} {
      font-size: 1rem;
    }
  }
`;

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${size(1)};
`;

export const RightColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  max-height: 100%;
  margin-left: auto;
  ${theme.screens.LtSm} {
    justify-content: center;
  }
`;

export const TestfreaksItemWrapper = styled.div`
  div {
    display: flex;
    align-items: center;
  }
`;
